import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { FileSearch, ScrollText } from 'lucide-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Loader } from '@/components/ui/Loader'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/inspections/LeadBadges'
import { SberAutoLeadDialog } from '@/components/inspections/SberAutoLeadDialog'
import { PARTNER_SBER_INTAKE } from '@/components/inspections/sberLeadIntake'
import { emptyTableCellClassName } from '@/constants/ui'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import {
  getPartnerMe,
  listPartnerLeads,
  partnerAutotekaUrl,
  partnerProtocolUrl,
  partnerStatuses,
  partnerStatusLabelKey,
  type PartnerLeadRow,
} from '@/api/partner'
import { PdfViewer, type PdfViewerState } from '@/components/partner/PdfViewer'
import type { InspectionLeadStatus, LeadPipeline } from '@/types/inspection-lead'
import { formatMoscowDateTime } from '@/utils/format'
import { formatLicensePlate } from '@/utils/licensePlate'

const PAGE = 50

type Filter = InspectionLeadStatus | 'all'

// Сортировка таблицы: клиентская, по загруженным строкам (как в списке лидов
// консоли). Ключи колонок = ключи подписей partner.col.<key>.
type SortKey = 'no' | 'crm' | 'client' | 'vehicle' | 'plate' | 'status' | 'docs' | 'updated'
type SortDir = 'asc' | 'desc'

const TABLE_COLUMNS: SortKey[] = ['no', 'crm', 'client', 'vehicle', 'plate', 'status', 'docs', 'updated']

// Статус сортируем по позиции в воронке партнёра; пустые значения всегда внизу.
function sortValue(r: PartnerLeadRow, key: SortKey, statuses: InspectionLeadStatus[]): string | number | null {
  switch (key) {
    case 'no':
      return r.lead_no
    case 'crm':
      return r.external_id ?? null
    case 'client':
      return r.client_name?.trim() || null
    case 'vehicle':
      return [r.vehicle_brand, r.vehicle_model, r.vehicle_year].filter(Boolean).join(' ') || null
    case 'plate':
      return r.license_plate ?? null
    case 'status':
      return statuses.indexOf(r.status)
    case 'docs':
      // Сортировка по числу документов: сначала с протоколом и Автотекой.
      return (r.has_protocol ? 2 : 0) + (r.has_autoteka ? 1 : 0)
    case 'updated':
      return new Date(r.status_changed_at).getTime()
  }
}

function compareForSort(a: string | number | null, b: string | number | null, dir: SortDir): number {
  const aEmpty = a === null || a === ''
  const bEmpty = b === null || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  const cmp = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'ru')
  return dir === 'asc' ? cmp : -cmp
}

/** /partner — заявки партнёра: поиск, фильтр по статусу, таблица, новая заявка. */
export function PartnerLeadsPage() {
  return (
    <DocViewerProvider>
      <PartnerLeadsPageInner />
    </DocViewerProvider>
  )
}

function PartnerLeadsPageInner() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useQuery({ queryKey: ['partner-me'], queryFn: getPartnerMe, staleTime: 5 * 60_000 })
  const pipeline: LeadPipeline = me.data?.pipeline ?? 'sberauto'
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [limit, setLimit] = useState(PAGE)
  const [creating, setCreating] = useState(false)
  const q = useDebouncedValue(search.trim(), 300)

  const list = useQuery({
    queryKey: ['partner-leads', q, filter, limit],
    queryFn: () => listPartnerLeads({ search: q, status: filter === 'all' ? null : filter, limit }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  })
  const items = useMemo(() => list.data?.items ?? [], [list.data])
  const counts = list.data?.counts ?? {}
  const all = Object.values(counts).reduce((a, n) => a + (n ?? 0), 0)
  const statusLabel = (s: InspectionLeadStatus) => t(partnerStatusLabelKey(s, pipeline))

  // Клик по заголовку: asc, повторный клик: desc; без выбора остаётся порядок
  // с сервера (created_at desc).
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const sortedItems = useMemo(() => {
    if (!sortKey) return items
    const statuses = partnerStatuses(pipeline)
    return [...items].sort((a, b) =>
      compareForSort(sortValue(a, sortKey, statuses), sortValue(b, sortKey, statuses), sortDir),
    )
  }, [items, sortKey, sortDir, pipeline])
  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  // Фильтр — только статусы, в которых есть заявки (и выбранный).
  const filterOptions = [
    { value: 'all' as Filter, label: t('partner.all'), count: all },
    ...partnerStatuses(pipeline)
      .filter((s) => (counts[s] ?? 0) > 0 || s === filter)
      .map((s) => ({ value: s as Filter, label: statusLabel(s), count: counts[s] ?? 0 })),
  ]

  return (
    <div>
      <PageHeader
        title={t('partner.title', { pipeline: me.data?.pipeline_title ?? '' })}
        actions={
          me.data?.can_create ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('partner.new')}
            </Button>
          ) : null
        }
        filters={
          <>
            <SearchInput
              className="min-w-0 flex-1 !max-w-none"
              placeholder={t('partner.search')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setLimit(PAGE)
              }}
            />
            <div className="-mx-4 flex w-[calc(100%+2rem)] gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:w-full sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0" role="radiogroup" aria-label={t('partner.col.status')}>
              {filterOptions.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={filter === o.value}
                  onClick={() => {
                    setFilter(o.value)
                    setLimit(PAGE)
                  }}
                  className={clsx(
                    'inline-flex min-h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 text-sm transition-colors',
                    filter === o.value
                      ? 'border-[color:var(--stryzh-color-accent-orange)] bg-[rgba(247,147,26,0.12)] text-[color:var(--stryzh-color-text-primary)]'
                      : 'border-[color:var(--stryzh-color-border-default)] text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]',
                  )}
                >
                  {o.label}
                  <span className="font-[family-name:var(--stryzh-font-family-mono)] text-xs opacity-70">{o.count}</span>
                </button>
              ))}
            </div>
          </>
        }
      />

      {/* Телефон: карточки вместо таблицы — статус виден без горизонтальной прокрутки. */}
      <div className="flex flex-col gap-2 md:hidden">
        {list.isLoading ? (
          <Loader centered size="sm" className="py-8" />
        ) : list.error ? (
          <div className={emptyTableCellClassName}>{(list.error as Error).message}</div>
        ) : items.length === 0 ? (
          <div className={emptyTableCellClassName}>{q || filter !== 'all' ? t('partner.notFound') : t('partner.empty')}</div>
        ) : (
          items.map((r) => <PartnerLeadCard key={r.id} row={r} pipeline={pipeline} />)
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)] md:block">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              {TABLE_COLUMNS.map((key) => {
                const label = t(`partner.col.${key}`)
                return (
                  <th key={key} className={clsx('whitespace-nowrap px-2 py-2', key === 'no' && 'w-[64px]')}>
                    <button
                      type="button"
                      onClick={() => onSort(key)}
                      className="inline-flex items-center gap-1 hover:text-[color:var(--stryzh-color-text-primary)]"
                      aria-label={t('leads.list.sortBy', { col: label })}
                    >
                      {label}
                      <span className="text-[10px] leading-none opacity-70">
                        {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {list.isLoading ? (
              <tr>
                <td colSpan={9} className={emptyTableCellClassName}>
                  <Loader centered size="sm" />
                </td>
              </tr>
            ) : list.error ? (
              <tr>
                <td colSpan={9} className={emptyTableCellClassName}>
                  {(list.error as Error).message}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} className={emptyTableCellClassName}>
                  {q || filter !== 'all' ? t('partner.notFound') : t('partner.empty')}
                </td>
              </tr>
            ) : (
              sortedItems.map((r) => (
                <PartnerLeadTableRow
                  key={r.id}
                  row={r}
                  pipeline={pipeline}
                  onOpen={() => navigate(`/partner/leads/${r.id}`)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {list.data && list.data.total > items.length ? (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" disabled={list.isFetching} onClick={() => setLimit((n) => n + PAGE)}>
            {t('partner.more', { n: list.data.total - items.length })}
          </Button>
        </div>
      ) : null}

      {creating ? (
        <SberAutoLeadDialog
          intake={PARTNER_SBER_INTAKE}
          onClose={() => setCreating(false)}
          onSaved={(r) => {
            setCreating(false)
            navigate(`/partner/leads/${r.leadId}`)
          }}
        />
      ) : null}
    </div>
  )
}

function PartnerLeadTableRow({
  row,
  pipeline,
  onOpen,
}: {
  row: PartnerLeadRow
  pipeline: LeadPipeline
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const vehicle = [row.vehicle_brand, row.vehicle_model, row.vehicle_year].filter(Boolean).join(' ')
  const muted = 'text-[color:var(--stryzh-color-text-secondary)]'
  return (
    <tr
      onClick={onOpen}
      className="cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
    >
      <td className="px-2 py-2 font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-primary)]">
        {row.lead_no}
      </td>
      <td className={`px-2 py-2 font-[family-name:var(--stryzh-font-family-mono)] ${muted}`}>{row.external_id ?? '—'}</td>
      <td className="max-w-[180px] truncate px-2 py-2">{row.client_name ?? '—'}</td>
      <td className="whitespace-nowrap px-2 py-2 font-medium text-[color:var(--stryzh-color-text-primary)]">{vehicle || '—'}</td>
      <td className={`whitespace-nowrap px-2 py-2 font-[family-name:var(--stryzh-font-family-mono)] ${muted}`}>
        {row.license_plate ? formatLicensePlate(row.license_plate) : '—'}
      </td>
      <td className="whitespace-nowrap px-2 py-2">
        <StatusBadge status={row.status} label={t(partnerStatusLabelKey(row.status, pipeline))} />
      </td>
      <td className="whitespace-nowrap px-2 py-2">
        <DocIcons row={row} />
      </td>
      <td className={`whitespace-nowrap px-2 py-2 ${muted}`}>{formatMoscowDateTime(row.status_changed_at)}</td>
    </tr>
  )
}

type DocKind = 'autoteka' | 'protocol'
const DocOpenContext = createContext<(row: PartnerLeadRow, kind: DocKind) => void>(() => {})

/** Просмотрщик документов для списка: один на страницу, иконки в строках
 *  открывают его через контекст (строка и карточка не тянут лишние пропсы). */
function DocViewerProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [viewer, setViewer] = useState<PdfViewerState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const open = async (row: PartnerLeadRow, kind: DocKind) => {
    setError(null)
    try {
      const urls =
        kind === 'autoteka'
          ? await partnerAutotekaUrl(row.autoteka_pdf_path as string, row.lead_no)
          : await partnerProtocolUrl(row.protocol_path as string, row)
      setViewer({ title: `${t(kind === 'autoteka' ? 'partner.lead.autotekaTitle' : 'partner.lead.protocolTitle')} · №${row.lead_no}`, urls })
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <DocOpenContext.Provider value={(row, kind) => void open(row, kind)}>
      {children}
      {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
      {viewer ? <PdfViewer title={viewer.title} urls={viewer.urls} onClose={() => setViewer(null)} /> : null}
    </DocOpenContext.Provider>
  )
}

/** Индикаторы документов: отчёт Автотеки и протокол торгов. Есть документ:
 *  бирюзовая кнопка, клик открывает просмотрщик; нет: тусклая иконка. */
export function DocIcons({ row, className }: { row: PartnerLeadRow; className?: string }) {
  const { t } = useTranslation()
  const openDoc = useContext(DocOpenContext)
  const on = 'text-[color:var(--stryzh-color-accent-carlink)] hover:text-[color:var(--stryzh-color-text-primary)]'
  const off = 'text-[color:var(--stryzh-color-border-default)]'
  const icon = (kind: DocKind, has: boolean, Icon: typeof FileSearch) => {
    const label = t(has ? `partner.docs.${kind}` : kind === 'autoteka' ? 'partner.docs.noAutoteka' : 'partner.docs.noProtocol')
    return has ? (
      <button
        type="button"
        className={clsx('inline-flex', on)}
        title={label}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation()
          openDoc(row, kind)
        }}
      >
        <Icon size={16} aria-hidden />
      </button>
    ) : (
      <span className={clsx('inline-flex', off)} title={label} aria-label={label}>
        <Icon size={16} aria-hidden />
      </span>
    )
  }
  return (
    <span className={clsx('inline-flex items-center gap-1.5', className)}>
      {icon('autoteka', row.has_autoteka, FileSearch)}
      {icon('protocol', row.has_protocol, ScrollText)}
    </span>
  )
}

function PartnerLeadCard({ row, pipeline }: { row: PartnerLeadRow; pipeline: LeadPipeline }) {
  const { t } = useTranslation()
  const vehicle = [row.vehicle_brand, row.vehicle_model, row.vehicle_year].filter(Boolean).join(' ')
  const muted = 'text-[color:var(--stryzh-color-text-secondary)]'
  const meta = [
    row.external_id ? t('partner.lead.crm', { id: row.external_id }) : null,
    row.license_plate ? formatLicensePlate(row.license_plate) : null,
  ].filter(Boolean)
  return (
    <Link
      to={`/partner/leads/${row.id}`}
      className="block rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4 text-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 font-medium text-[color:var(--stryzh-color-text-primary)]">
          <span className="font-[family-name:var(--stryzh-font-family-mono)]">{t('partner.lead.no', { no: row.lead_no })}</span>
          {vehicle ? ` · ${vehicle}` : ''}
        </div>
        <span className="inline-flex items-center gap-2">
          <DocIcons row={row} />
          <StatusBadge status={row.status} label={t(partnerStatusLabelKey(row.status, pipeline))} />
        </span>
      </div>
      {row.client_name ? <div className="mt-2 truncate text-[color:var(--stryzh-color-text-primary)]">{row.client_name}</div> : null}
      {meta.length ? (
        <div className={`mt-1 font-[family-name:var(--stryzh-font-family-mono)] text-xs ${muted}`}>{meta.join(' · ')}</div>
      ) : null}
      <div className={`mt-2 flex flex-wrap justify-between gap-x-3 text-xs ${muted}`}>
        <span />
        <span>{formatMoscowDateTime(row.status_changed_at)}</span>
      </div>
    </Link>
  )
}
